import digitalStyleGuideScreens, {
  digitalStyleGuideFoundationGallery,
} from "../../digitalStyleGuideMedia";

const digitalStyleGuide = {
  id: "digital-style-guide",
  slug: "digital-style-guide",
  category: "Foundations",
  navTitle: "Creating the Digital Style Guide",
  title: "Creating the Digital Style Guide",
  introduction: [
    "Co-operators had completed a major rebrand, but the resulting identity was designed primarily for traditional media. I led the translation of that work into practical digital standards for colour, typography, accessibility, and interface use across web and mobile products.",
  ],
  challenge: [
    {
      title: "A print-focused brand",
      paragraphs: [
        "The new identity provided a strong visual direction, but it did not fully address responsive interfaces, interaction states, screen readability, or digital accessibility.",
      ],
    },
    {
      title: "Accessibility limitations",
      paragraphs: [
        "Several colour combinations did not provide enough contrast for text and controls, especially across light and dark experiences.",
      ],
    },
    {
      title: "Typography constraints",
      paragraphs: [
        "The proprietary brand typeface worked well in print but created concerns around small-screen readability, implementation, licensing, and long-term maintenance.",
      ],
    },
    {
      title: "Inconsistent interpretation",
      paragraphs: [
        "Without clear digital guidance, teams could apply the brand differently across websites, applications, and content platforms.",
      ],
    },
  ],
  process: [
    {
      title: "Determining what should carry forward",
      paragraphs: [
        "I began by separating the core characteristics of the brand from the print treatments used to express them.",
      ],
      bullets: [
        "Reviewed colour, typography, hierarchy, and visual treatments in responsive contexts.",
        "Identified gaps related to accessibility, interaction states, content density, and component behaviour.",
        "Used usability, accessibility, consistency, and implementation feasibility as decision criteria.",
      ],
      closing:
        "The objective was not to copy the print brand onto a screen. It was to preserve the character of the identity while allowing it to work properly in digital products.",
    },
    {
      title: "Adapting colour and typography",
      paragraphs: [
        "I worked through the areas where the original brand decisions created the most friction.",
      ],
      bullets: [
        "Tested colour combinations against WCAG requirements.",
        "Defined where colours could be used for text, controls, backgrounds, alerts, and interaction states.",
        "Created accessible options for light and dark modes.",
        "Evaluated the proprietary typeface for screen use, implementation, and licensing.",
        "Introduced an open-source digital font with similar characteristics.",
        "Created a practical hierarchy for headings, body copy, labels, and controls.",
      ],
      closing:
        "This created continuity without forcing the same solution across every medium.",
    },
    {
      title: "Building the guide",
      paragraphs: [
        "I created a shared reference for design, development, content, and platform teams.",
      ],
      bullets: [
        "Defined standards for colour, typography, spacing, hierarchy, and accessibility.",
        "Included practical examples for web and mobile.",
        "Explained the reasoning behind the standards, not just the rules.",
        "Validated the guidance with teams responsible for implementing it.",
      ],
    },
    {
      title: "Making it part of delivery",
      paragraphs: [
        "The guide was connected directly to the systems teams already used.",
      ],
      bullets: [
        "Aligned the standards with Oak.",
        "Carried the guidance into the Sitecore website.",
        "Delivered documentation and training.",
        "Used feedback from active work to identify where the guidance was unclear or incomplete.",
      ],
      closing:
        "The guide became part of how products were built rather than a document teams were expected to interpret on their own.",
    },
  ],
  outcomes: [
    {
      title: "A practical digital expression of the brand",
      description:
        "The identity became usable across responsive web and mobile experiences without losing its recognizable character.",
    },
    {
      title: "Improved accessibility",
      description:
        "Teams had clear, tested colour and typography options for creating accessible interfaces.",
    },
    {
      title: "Greater consistency",
      description:
        "Designers, developers, content teams, and platform partners had one shared reference for applying the brand.",
    },
    {
      title: "More efficient implementation",
      description:
        "Embedding approved decisions into Oak reduced repeated debate and made consistent implementation easier.",
    },
    {
      title: "A more sustainable typography approach",
      description:
        "The open-source digital font improved readability and simplified licensing and maintenance.",
    },
  ],
  reflection: [
    "This work reinforced that translating a brand across mediums is not an exercise in replication.",
    "Sometimes preserving the identity requires changing its literal expression. The test is whether the brand still feels coherent while working effectively for the people using it.",
    "The most important result was a shared decision framework that helped teams balance brand, usability, accessibility, and technical practicality in everyday work.",
  ],
  media: {
    blocks: [
      {
        id: "digital-style-guide-foundations-composition",
        type: "composition",
        placement: "afterIntro",
        className: "work-page__media-block--integrated-opening",
        items: digitalStyleGuideScreens,
        aspectRatio: "3 / 2",
        label: "Co-operators Digital Standards colour, typography, and interface foundations",
        galleryLabel: "Co-operators Digital Standards foundations gallery",
        alt: "Co-operators Digital Standards documentation for colour, typography, and interface styles",
      },
      {
        id: "digital-style-guide-colour-and-type",
        type: "grid",
        placement: "afterChallenge",
        title: "Translating the brand for digital use",
        description:
          "Accessible colour roles and a practical screen typography hierarchy preserved the character of the identity while making it usable across responsive products.",
        layout: "default",
        options: {
          maxWidth: 1000,
          tileMin: 320,
          thumbHeight: 440,
          thumbFit: "contain",
          modalMaxWidth: 1180,
          modalMaxHeight: "min(90vh, 960px)",
        },
        items: digitalStyleGuideFoundationGallery,
      },
    ],
  },
};

export default digitalStyleGuide;
